import { useCallback, useEffect, useRef, type SyntheticEvent } from "react";
import { logOverlayDebug } from "@/lib/overlay-guard";
import { measureTriggerHitbox } from "@/lib/menu-hitbox";


type MenuDiagnosticsState = {
  open: boolean;
};

export function useMenuDiagnostics(menuName: string, state: MenuDiagnosticsState) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const log = useCallback(
    (eventName: string, event?: Event | SyntheticEvent) => {
      const nativeEvent = "nativeEvent" in (event || {}) ? (event as SyntheticEvent).nativeEvent : (event as Event | undefined);
      logOverlayDebug(`${menuName}:${eventName}`, nativeEvent, {
        menuName,
        menuOpen: stateRef.current.open,
        triggerMounted: !!triggerRef.current,
      });
    },
    [menuName],
  );

  const logTriggerPoint = useCallback(
    (label: string) => {
      const trigger = triggerRef.current;
      measureTriggerHitbox(trigger, `${menuName}:${label}`);
      if (!trigger) {
        logOverlayDebug(`${menuName}:${label}:no-trigger`, undefined, {
          menuName,
          menuOpen: stateRef.current.open,
          triggerMounted: false,
        });
        return;
      }
      const rect = trigger.getBoundingClientRect();
      logOverlayDebug(`${menuName}:${label}`, undefined, {
        menuName,
        menuOpen: stateRef.current.open,
        triggerMounted: true,
        triggerRect: {
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        },
        topAtTriggerCenter:
          typeof document !== "undefined"
            ? (() => {
                const element = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
                if (!element) return null;
                return {
                  tag: element.tagName.toLowerCase(),
                  id: element.id || null,
                  role: element.getAttribute("role"),
                  state: element.getAttribute("data-state"),
                  menuTrigger: element.getAttribute("data-menu-trigger"),
                  classes:
                    typeof element.className === "string"
                      ? element.className.trim().split(/\s+/).slice(0, 6)
                      : [],
                };
              })()
            : null,
      });
    },
    [menuName],
  );

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        window.setTimeout(() => logTriggerPoint("visible"), 0);
        window.setTimeout(() => logTriggerPoint("visible-after-paint"), 350);
      }
    };
    const onFocus = () => window.setTimeout(() => logTriggerPoint("focus"), 0);
    const onPageShow = () => window.setTimeout(() => logTriggerPoint("pageshow"), 0);

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    window.setTimeout(() => logTriggerPoint("mounted"), 0);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [logTriggerPoint, menuName]);


  return { triggerRef, log, logTriggerPoint };
}